import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { 
  MessageCircle, 
  X, 
  Bell, 
  Users, 
  AlertTriangle, 
  Info,
  Plus,
  Send
} from 'lucide-react'
import {
  MessagesContainer,
  MessagesFab,
  NotificationBadge,
  MessagesModal,
  MessagesHeader,
  MessagesTitle,
  MessagesSubtitle,
  MessagesList,
  MessageCard,
  MessageHeader,
  MessageAuthor,
  AuthorAvatar,
  AuthorInfo,
  AuthorName,
  AuthorRole,
  MessageTime,
  MessageContent,
  MessageType,
  EmptyMessages,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription,
  MessagesFooter,
  WriteMessageButton,
  CloseButton,
  ToastNotification,
  ToastIcon,
  ToastContent,
  ToastTitle,
  ToastMessage
} from './styled'

// Mock data - replace with your API calls
const mockMessages = [
  {
    id: 1,
    author: {
      name: "Event Organizer",
      role: "Host",
      avatar: "E",
      isOrganizer: true
    },
    content: "Welcome everyone! We're excited to have you join us for this amazing event. Please check your email for the venue access instructions.",
    type: "announcement",
    timestamp: "2 hours ago",
    isNew: false
  },
  {
    id: 2,
    author: {
      name: "Sarah Wilson",
      role: "Attendee",
      avatar: "S",
      isOrganizer: false
    },
    content: "Looking forward to the networking session! Anyone interested in discussing AI and machine learning?",
    type: "message",
    timestamp: "1 hour ago",
    isNew: true
  },
  {
    id: 3,
    author: {
      name: "Event Team",
      role: "Staff",
      avatar: "T",
      isOrganizer: true
    },
    content: "⚠️ Important: The main entrance will be closed from 2-3 PM. Please use the side entrance during this time.",
    type: "urgent",
    timestamp: "30 minutes ago",
    isNew: true
  },
  {
    id: 4,
    author: {
      name: "Tech Support",
      role: "Support",
      avatar: "T",
      isOrganizer: true
    },
    content: "WiFi credentials have been updated. Network: EventWiFi2024, Password: Connect2024!",
    type: "update",
    timestamp: "15 minutes ago",
    isNew: true
  }
]

const EventMessagesSystem = ({ eventId, currentUser }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState(mockMessages)
  const [unreadCount, setUnreadCount] = useState(0)
  const [showToast, setShowToast] = useState(false)
  const [latestMessage, setLatestMessage] = useState(null)

  // Calculate unread count
  useEffect(() => {
    const newMessages = messages.filter(msg => msg.isNew)
    setUnreadCount(newMessages.length)
  }, [messages])

  // Simulate receiving new messages
  useEffect(() => {
    const interval = setInterval(() => {
      // Simulate getting a new message (replace with real API polling or WebSocket)
      if (Math.random() > 0.7) { // 30% chance every 10 seconds
        const newMessage = {
          id: Date.now(),
          author: {
            name: "Event Updates",
            role: "System",
            avatar: "🔔",
            isOrganizer: true
          },
          content: "New update: Coffee break has been extended by 15 minutes. Enjoy!",
          type: "update",
          timestamp: "Just now",
          isNew: true
        }
        
        setMessages(prev => [newMessage, ...prev])
        setLatestMessage(newMessage)
        setShowToast(true)
        
        // Auto-hide toast after 5 seconds
        setTimeout(() => setShowToast(false), 5000)
      }
    }, 10000) // Check every 10 seconds
    
    return () => clearInterval(interval)
  }, [])

  const handleOpenMessages = () => {
    setIsOpen(true)
    // Mark messages as read when opened
    setMessages(prev => prev.map(msg => ({ ...msg, isNew: false })))
    setUnreadCount(0)
  }

  const handleCloseMessages = () => {
    setIsOpen(false)
  }

  const handleToastClick = () => {
    setShowToast(false)
    handleOpenMessages()
  }

  const formatTimeAgo = (timestamp) => {
    // In a real app, you'd use a proper date formatting library
    return timestamp
  }

  const getMessageIcon = (type) => {
    switch(type) {
      case 'announcement': return <Bell size={14} />
      case 'urgent': return <AlertTriangle size={14} />
      case 'update': return <Info size={14} />
      default: return <MessageCircle size={14} />
    }
  }

  const handleSendMessage = () => {
    // Implement send message functionality
  }

  return (
    <>
      <MessagesContainer>
        {/* Floating Action Button */}
        <MessagesFab onClick={handleOpenMessages}>
          <MessageCircle size={24} color="white" />
          {unreadCount > 0 && (
            <NotificationBadge>
              {unreadCount > 99 ? '99+' : unreadCount}
            </NotificationBadge>
          )}
        </MessagesFab>

        {/* Messages Modal */}
        <MessagesModal isOpen={isOpen}>
          <MessagesHeader>
            <MessagesTitle>Event Messages</MessagesTitle>
            <MessagesSubtitle>
              Stay updated with announcements and connect with other attendees
            </MessagesSubtitle>
            <CloseButton onClick={handleCloseMessages}>
              <X size={18} />
            </CloseButton>
          </MessagesHeader>

          <MessagesList>
            {messages.length === 0 ? (
              <EmptyMessages>
                <EmptyIcon>
                  <MessageCircle size={32} color="rgba(255, 255, 255, 0.3)" />
                </EmptyIcon>
                <EmptyTitle>No messages yet</EmptyTitle>
                <EmptyDescription>
                  Messages from organizers and other attendees will appear here. 
                  Be the first to start a conversation!
                </EmptyDescription>
              </EmptyMessages>
            ) : (
              messages.map((message) => (
                <MessageCard key={message.id}>
                  <MessageHeader>
                    <MessageAuthor>
                      <AuthorAvatar>
                        {message.author.avatar}
                      </AuthorAvatar>
                      <AuthorInfo>
                        <AuthorName>{message.author.name}</AuthorName>
                        <AuthorRole>{message.author.role}</AuthorRole>
                      </AuthorInfo>
                    </MessageAuthor>
                    <MessageTime>{formatTimeAgo(message.timestamp)}</MessageTime>
                  </MessageHeader>
                  
                  <MessageContent>
                    {message.content}
                  </MessageContent>
                  
                  <MessageType type={message.type}>
                    {getMessageIcon(message.type)}
                    {message.type}
                  </MessageType>
                </MessageCard>
              ))
            )}
          </MessagesList>

          {/* Optional: Allow attendees to send messages */}
          <MessagesFooter>
            <WriteMessageButton onClick={handleSendMessage}>
              <Plus size={16} />
              Write a message
            </WriteMessageButton>
          </MessagesFooter>
        </MessagesModal>
      </MessagesContainer>

      {/* Toast Notification for new messages */}
      <ToastNotification isVisible={showToast} onClick={handleToastClick}>
        <ToastIcon>
          <MessageCircle size={18} />
        </ToastIcon>
        <ToastContent>
          <ToastTitle>New Message</ToastTitle>
          <ToastMessage>
            {latestMessage?.content}
          </ToastMessage>
        </ToastContent>
      </ToastNotification>
    </>
  )
}

EventMessagesSystem.propTypes = {
  eventId: PropTypes.string,
  currentUser: PropTypes.object,
}

EventMessagesSystem.defaultProps = {
  eventId: null,
  currentUser: null,
}

export default EventMessagesSystem